
import React, { useState } from 'react';
import PageLayout from '@/components/layout/PageLayout';
import PageHero from '@/components/ui/PageHero';
import ScrollReveal from '@/components/ui/ScrollReveal';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { CheckCircle, MapPin, Camera, Monitor, PenTool, Lightbulb, Mic, Video, BarChart3 } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { submitToPabbly } from '@/utils/pabblyConnect';
import { createLocalBusinessSchema, createServiceSchema, astremBusinessData } from '@/utils/seoUtils';

const GeismarLocation = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    service: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { id, value } = e.target;
    setFormData(prev => ({ ...prev, [id]: value }));
  };

  const servicesOffered = [
    {
      title: 'Professional Photography',
      description: 'High-quality photography services for Geismar businesses, real estate, events, and portraits.',
      icon: <Camera />,
      link: '/services/photography'
    },
    {
      title: 'Web Design & Development',
      description: 'Custom websites for Geismar businesses designed to convert visitors and represent your brand.',
      icon: <Monitor />,
      link: '/services/website-design'
    },
    {
      title: 'Branding & Identity',
      description: 'Comprehensive branding solutions to help Geismar businesses stand out in the market.',
      icon: <PenTool />,
      link: '/services/branding'
    },
    {
      title: 'Digital Marketing',
      description: 'Strategic marketing services to help Geismar businesses reach their target audience effectively.',
      icon: <BarChart3 />,
      link: '/services/marketing'
    },
    {
      title: 'LED Printing',
      description: 'Innovative LED printing technology for Geismar businesses looking to create memorable marketing materials.',
      icon: <Lightbulb />,
      link: '/services/led-printing'
    },
    {
      title: 'Tech Coaching',
      description: 'Personalized technology coaching to help Geismar entrepreneurs and professionals leverage digital tools.',
      icon: <Mic />,
      link: '/services/tech-coaching'
    }
  ];

  const geismarFacts = [
    'Home to major industrial facilities including BASF, Shell, and other chemical plants',
    'Located in Ascension Parish along the Mississippi River',
    'Part of the growing Baton Rouge metropolitan area',
    'Has a strong industrial economy with high-paying jobs',
    'Features a growing residential community',
    'Strategically positioned between Baton Rouge and New Orleans along the industrial corridor'
  ];

  const testimonials = [
    {
      name: "Thomas Reynolds",
      title: "Industrial Safety Manager",
      quote: "Evan's photography services for our industrial facility were exceptional. He was able to capture our operations while adhering to all safety protocols. The images have been invaluable for our marketing materials."
    },
    {
      name: "Karen Miller",
      title: "Geismar Business Owner",
      quote: "The website Astrem created for my business has helped us connect with both industrial clients and local residents. Evan's understanding of the unique Geismar market was critical to our success."
    }
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      await submitToPabbly(
        { ...formData, location: 'Geismar' },
        'geismar_location_form'
      );
      
      toast({
        title: "Message sent!",
        description: "Thank you for reaching out. We'll be in touch soon.",
      });
      
      // Reset form
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        service: '',
        message: ''
      });
    } catch (error) {
      console.error('Form submission error:', error);
      toast({
        title: "Error sending message",
        description: "There was a problem sending your message. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Geismar-specific business data
  const geismarBusinessData = {
    ...astremBusinessData,
    name: "Astrem - Geismar, Louisiana",
    description: "Astrem provides websites, photography, marketing, and branding services to the Geismar, Louisiana area. Let's build your digital presence together.",
    address: {
      streetAddress: "123 Main St", 
      addressLocality: "Geismar",
      addressRegion: "LA",
      postalCode: "70734",
      addressCountry: "US"
    },
    geo: {
      latitude: 30.2566,
      longitude: -91.0115
    }
  };

  // Services offered in Geismar
  const geismarServices = [
    {
      name: "Website Design in Geismar, LA",
      description: "Professional website design services for Geismar, Louisiana businesses and organizations. Stand out from the crowd with a website that attracts and converts customers.",
      provider: {
        name: "Astrem",
        url: "https://astrem.co"
      },
      serviceArea: {
        name: "Geismar, LA and surrounding areas",
      },
      url: "https://astrem.co/services/website-design"
    },
    {
      name: "Photography Services in Geismar, LA",
      description: "Expert photography services for businesses, real estate, events, and more in Geismar, Louisiana.",
      provider: {
        name: "Astrem",
        url: "https://astrem.co"
      },
      serviceArea: {
        name: "Geismar, LA and surrounding areas",
      },
      url: "https://astrem.co/services/photography"
    },
    {
      name: "Marketing Services in Geismar, LA",
      description: "Digital marketing services for Geismar businesses looking to grow their online presence and attract more customers.",
      provider: {
        name: "Astrem",
        url: "https://astrem.co"
      },
      serviceArea: {
        name: "Geismar, LA and surrounding areas",
      },
      url: "https://astrem.co/services/marketing"
    }
  ];

  // Create schema.org data
  const localBusinessSchema = createLocalBusinessSchema(geismarBusinessData);
  const serviceSchemas = geismarServices.map(service => createServiceSchema(service));
  const allSchemas = [localBusinessSchema, ...serviceSchemas];

  return (
    <PageLayout 
      title="Astrem | Geismar Web Design, Photography & Marketing Services" 
      description="Professional web design, photography, marketing, and technology services in Geismar, Louisiana. Helping local businesses grow with custom digital solutions."
      schemaData={allSchemas}
      ogImage="/lovable-uploads/b19346aa-736e-4cf9-a17d-17475e2b916a.png"
    >
      <PageHero 
        title="Geismar Digital Services" 
        subtitle="Local Expertise, Exceptional Results"
        description="Professional web design, photography, marketing, and technology services tailored for Geismar businesses and individuals. Partner with a local expert who understands the unique needs of our industrial community."
        backgroundImage="https://images.unsplash.com/photo-1564979045531-fa386a275b27?q=80&w=1000&auto=format&fit=crop"
        ctaText="Contact Us"
        ctaLink="/contact"
      />

      <section className="py-10 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {testimonials.map((testimonial, index) => (
              <ScrollReveal key={index} delay={index * 100}>
                <div className="bg-white p-6 rounded-lg border border-astrem-gray-200 shadow-sm">
                  <p className="text-astrem-gray-600 italic mb-4">"{testimonial.quote}"</p>
                  <div>
                    <h4 className="font-medium">{testimonial.name}</h4>
                    <p className="text-sm text-astrem-gray-600">{testimonial.title}</p>
                  </div>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-10 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal>
            <div className="max-w-3xl mx-auto text-center mb-8">
              <span className="inline-block px-3 py-1 bg-astrem-gray-100 text-astrem-gray-800 rounded-full text-sm font-medium mb-4">
                Our Location
              </span>
              <h2 className="text-2xl md:text-3xl font-display font-medium mb-6">Proudly Serving Geismar, Louisiana</h2>
            </div>
          </ScrollReveal>
          
          <ScrollReveal delay={100}>
            <div className="relative h-96 rounded-lg overflow-hidden shadow-lg">
              <iframe 
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d55050.21111111111!2d-91.01150000000002!3d30.25660000000001!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x86242fc31df35f41%3A0x6e9cf241c90f24b5!2sGeismar%2C%20LA!5e0!3m2!1sen!2sus!4v1712579694353!5m2!1sen!2sus" 
                width="100%" 
                height="100%" 
                style={{ border: 0 }} 
                allowFullScreen 
                loading="lazy" 
                referrerPolicy="no-referrer-when-downgrade"
              ></iframe>
            </div>
          </ScrollReveal>
        </div>
      </section>

      <section className="py-20 bg-astrem-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal>
            <div className="max-w-3xl mx-auto text-center mb-16">
              <span className="inline-block px-3 py-1 bg-astrem-gray-100 text-astrem-gray-800 rounded-full text-sm font-medium mb-4">
                Our Services
              </span>
              <h2 className="text-3xl md:text-4xl font-display font-medium mb-6">What We Offer in Geismar</h2>
              <p className="text-lg text-astrem-gray-600">
                We provide comprehensive digital solutions to help Geismar businesses and individuals establish a strong local presence and achieve their goals.
              </p>
            </div>
          </ScrollReveal>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {servicesOffered.map((service, index) => (
              <ScrollReveal key={index} delay={index * 100}>
                <Card className="h-full border-0 shadow-md hover:shadow-lg transition-shadow">
                  <CardContent className="p-6 flex flex-col h-full">
                    <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mb-4">
                      <div className="text-primary">
                        {service.icon}
                      </div>
                    </div>
                    <h3 className="text-xl font-medium mb-3">{service.title}</h3>
                    <p className="text-astrem-gray-600 mb-6 flex-grow">{service.description}</p>
                    <Button asChild variant="outline" className="w-full rounded-full mt-auto">
                      <Link to={service.link}>
                        Learn More
                      </Link>
                    </Button>
                  </CardContent>
                </Card>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <ScrollReveal>
              <div>
                <span className="inline-block px-3 py-1 bg-astrem-gray-100 text-astrem-gray-800 rounded-full text-sm font-medium mb-4">
                  About Our City
                </span>
                <h2 className="text-3xl md:text-4xl font-display font-medium mb-6">
                  Geismar, Louisiana
                </h2>
                <p className="text-lg text-astrem-gray-600 mb-8">
                  Geismar is a key industrial community in Ascension Parish, positioned along the Mississippi River. Home to major chemical and manufacturing facilities, Geismar plays a crucial role in Louisiana's industrial economy while maintaining a small-town community feel for its residents.
                </p>
                
                <div className="space-y-3 mb-8">
                  {geismarFacts.map((fact, index) => (
                    <div key={index} className="flex items-start">
                      <div className="flex-shrink-0 mr-3 mt-1">
                        <CheckCircle className="h-5 w-5 text-primary" />
                      </div>
                      <p className="text-astrem-gray-600">{fact}</p>
                    </div>
                  ))}
                </div>
                
                <p className="text-lg text-astrem-gray-600">
                  As someone who understands the unique nature of Geismar's industrial landscape and local community, I provide tailored digital solutions that address the specific needs of both industrial businesses and local residents. My experience working with industrial clients ensures that I can deliver results while understanding safety protocols and business requirements.
                </p>
              </div>
            </ScrollReveal>
            
            <ScrollReveal delay={200}>
              <div className="space-y-6">
                <img
                  src="https://images.unsplash.com/photo-1504917595217-d4dc5ebe6122?q=80&w=1000&auto=format&fit=crop"
                  alt="Industrial facility"
                  className="rounded-lg shadow-lg w-full h-auto object-cover"
                />
                <div className="grid grid-cols-2 gap-6">
                  <img
                    src="https://images.unsplash.com/photo-1589277880513-b9e485e3cccd?q=80&w=1000&auto=format&fit=crop"
                    alt="Geismar riverside"
                    className="rounded-lg shadow-lg w-full h-auto object-cover"
                  />
                  <img
                    src="https://portal.astrem.co/users/u9/9466/astremagency/lovableagentsite/Man_working_on_laptop_outside.jpg"
                    alt="Evan working in Geismar"
                    className="rounded-lg shadow-lg w-full h-auto object-cover"
                  />
                </div>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      <section className="py-20 bg-astrem-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal>
            <div className="max-w-3xl mx-auto text-center mb-16">
              <span className="inline-block px-3 py-1 bg-astrem-gray-100 text-astrem-gray-800 rounded-full text-sm font-medium mb-4">
                Our Goals
              </span>
              <h2 className="text-3xl md:text-4xl font-display font-medium mb-6">How We Help Geismar Businesses</h2>
              <p className="text-lg text-astrem-gray-600">
                We're committed to helping local businesses and organizations succeed in today's competitive digital landscape.
              </p>
            </div>
          </ScrollReveal>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <ScrollReveal delay={100}>
              <div className="bg-white p-8 rounded-lg shadow-md">
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mb-6">
                  <Monitor className="h-8 w-8 text-primary" />
                </div>
                <h3 className="text-xl font-medium mb-3">Establish a Strong Online Presence</h3>
                <p className="text-astrem-gray-600">
                  We help Geismar businesses create compelling websites and digital platforms that represent their brand effectively and attract both industrial and residential customers.
                </p>
              </div>
            </ScrollReveal>

            <ScrollReveal delay={200}>
              <div className="bg-white p-8 rounded-lg shadow-md">
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mb-6">
                  <MapPin className="h-8 w-8 text-primary" />
                </div>
                <h3 className="text-xl font-medium mb-3">Connect with Local Audiences</h3>
                <p className="text-astrem-gray-600">
                  We implement strategies specifically designed to help you reach and engage with Geismar customers through targeted marketing and content.
                </p>
              </div>
            </ScrollReveal>

            <ScrollReveal delay={300}>
              <div className="bg-white p-8 rounded-lg shadow-md">
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mb-6">
                  <Lightbulb className="h-8 w-8 text-primary" />
                </div>
                <h3 className="text-xl font-medium mb-3">Leverage Innovative Solutions</h3>
                <p className="text-astrem-gray-600">
                  We bring cutting-edge technologies and creative approaches to help your Geismar business stand out from competitors and drive growth.
                </p>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <ScrollReveal>
              <img
                src="https://portal.astrem.co/users/u9/9466/astremagency/lovableagentsite/Man_working_on_laptop_outside.jpg"
                alt="Evan portrait"
                className="rounded-lg shadow-lg w-full h-auto object-cover"
              />
            </ScrollReveal>
            
            <ScrollReveal delay={200}>
              <div>
                <span className="inline-block px-3 py-1 bg-astrem-gray-100 text-astrem-gray-800 rounded-full text-sm font-medium mb-4">
                  About Me
                </span>
                <h2 className="text-3xl md:text-4xl font-display font-medium mb-6">
                  Meet Evan
                </h2>
                <p className="text-lg text-astrem-gray-600 mb-6">
                  I'm a creative professional based in Louisiana specializing in web design, photography, marketing, and innovative technology solutions. With a deep understanding of the local market and a passion for helping businesses succeed, I bring a personalized approach to every project.
                </p>
                <p className="text-lg text-astrem-gray-600 mb-8">
                  Having worked with industrial clients in Geismar and surrounding areas, I understand the unique requirements of businesses in this sector. I'm committed to supporting Geismar businesses with digital solutions that meet their specific needs.
                </p>
                <Button asChild className="rounded-full">
                  <Link to="/about">
                    Learn More About Me
                  </Link>
                </Button>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      <section className="py-20 bg-astrem-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal>
            <div className="max-w-3xl mx-auto text-center mb-12">
              <span className="inline-block px-3 py-1 bg-astrem-gray-100 text-astrem-gray-800 rounded-full text-sm font-medium mb-4">
                Get in Touch
              </span>
              <h2 className="text-3xl md:text-4xl font-display font-medium mb-6">Contact Us</h2>
              <p className="text-lg text-astrem-gray-600">
                Ready to discuss how we can help your Geismar business succeed? Fill out the form below, and we'll get back to you promptly.
              </p>
            </div>
          </ScrollReveal>

          <ScrollReveal delay={100}>
            <div className="max-w-2xl mx-auto bg-white rounded-lg shadow-lg p-8 border border-astrem-gray-200">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="firstName">First Name *</Label>
                    <Input 
                      id="firstName" 
                      placeholder="John" 
                      value={formData.firstName}
                      onChange={handleChange}
                      required 
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="lastName">Last Name *</Label>
                    <Input 
                      id="lastName" 
                      placeholder="Doe" 
                      value={formData.lastName}
                      onChange={handleChange}
                      required 
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="email">Email *</Label>
                    <Input 
                      id="email" 
                      type="email" 
                      placeholder="john@example.com" 
                      value={formData.email}
                      onChange={handleChange}
                      required 
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone">Phone *</Label>
                    <Input 
                      id="phone" 
                      type="tel" 
                      placeholder="(555) 555-5555" 
                      value={formData.phone}
                      onChange={handleChange}
                      required 
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="service">Service Interested In *</Label>
                  <Input 
                    id="service" 
                    placeholder="Website Design, Photography, Marketing, etc." 
                    value={formData.service}
                    onChange={handleChange}
                    required 
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="message">Message</Label>
                  <Textarea 
                    id="message" 
                    placeholder="Tell us about your project or inquiry..." 
                    className="h-32"
                    value={formData.message}
                    onChange={handleChange}
                  />
                </div>
                
                <Button 
                  type="submit" 
                  className="w-full rounded-full"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "Sending..." : "Send Message"}
                </Button>
              </form>
            </div>
          </ScrollReveal>
        </div>
      </section>
    </PageLayout>
  );
};

export default GeismarLocation;
